<template>

<!--
	VERIFY (eyeball in `npm run serve` — do not production-build):
	1. Fresh start: one tile lifted (TILES 1/16), the rest covered with the
	   faint "BOX ART" label; meta reads "No. N · TILES 1/16 · GUESSES 0/16";
	   empty guess list + no hints.
	2. Mid-game: after a few wrong guesses, that many extra tiles dissolved (in
	   corners-first order), terracotta "Not it" rows listing the guessed names,
	   and the accent hint tiles (RANK then YEAR ...) appearing as they unlock.
	3. Finished: the End reveal (box art + score + stats + share) renders in
	   place of the play surface at the same URL (no /end route).
	Also: desktop (>=720px) shows art-left / play-rail-right; mobile is a single
	column. prefers-reduced-motion cuts the tile + hint animations.
-->

<!-- once the run finishes, the reveal renders in place (same URL, no /end
     route) — Wordle-style. Otherwise the play surface. -->
<end-screen v-if="reveal" />

<div v-else class="daily">

	<app-topbar />

	<main class="daily-body">

		<!-- while the puzzle loads (incl. switching from the archive) the
		     previous puzzle is cleared and the animated mark shows -->
		<div v-if="!today" class="daily-loading">
			<app-loader />
		</div>

		<div v-else class="daily-card">

			<div class="daily-stage">

				<!-- ART column -->
				<div class="daily-art">

					<play-frame />

					<div class="daily-art-meta">
						<play-meta />
					</div>

				</div>

				<!-- PLAY rail -->
				<div class="daily-rail">

					<div class="daily-prompt">
						<h1 class="daily-title">Which game is this?</h1>
						<p class="daily-sub">Each wrong guess dissolves another tile.</p>
					</div>

					<play-guess-input v-if="!finished" v-on:picked="onPicked" />

					<play-guess-list :names="names" />

					<!-- EXPERIMENT: hints now live on the grid as hint tiles, not
					     in a separate panel -->
					<!-- <play-hints v-if="!finished" /> -->

				</div>

			</div>

		</div>

	</main>

	<app-foot />

</div>

</template>

<script>

/*
	The daily puzzle screen: the chrome (Topbar / Foot) plus the play surface
	(Frame, Meta, GuessInput, GuessList, Hints) composed into one card. Mobile is
	a single column (art over the play rail); at >=720px it splits to art-left /
	play-rail-right per the desktop design.

	On mount it dispatches game.load(date) — `date` (a route param) drives a past
	puzzle in unranked practice; absent, it loads today. As the player picks a
	suggestion, GuessInput emits { id, name }; we cache that here so GuessList
	can label rows by name (game.js stores guessed ids only).

	When the puzzle finishes we render the End reveal (reveal + stats + share) IN
	PLACE of the play surface, at the same URL — there is no separate /end route.
	A win flips immediately; a loss holds a beat so the last tile reveal is seen;
	a reload of an already-finished puzzle reveals at once.
*/

import Frame from '../components/play/Frame.vue'
import GuessInput from '../components/play/GuessInput.vue'
import GuessList from '../components/play/GuessList.vue'
import Hints from '../components/play/Hints.vue'
import Meta from '../components/play/Meta.vue'
import End from './End.vue'

export default {

	name: 'Daily',

	components: {
		'play-frame': Frame,
		'play-guess-input': GuessInput,
		'play-guess-list': GuessList,
		'play-hints': Hints,
		'play-meta': Meta,
		'end-screen': End
	},

	props: {
		// a past puzzle date for practice; absent = today
		date: {
			default: null
		}
	},

	data: function() {

		return {
			// guessed game id -> name, captured as suggestions are picked, so
			// the guess rows can render names (the store keeps only ids)
			names: {},
			// once true, the End reveal renders in place of the play surface.
			// A live finish flips it after a short beat (so the last tile reveal
			// is seen); a reload of an already-finished puzzle flips it at once.
			reveal: false
		}

	},

	computed: {

		today: function() {

			return this.$store.getters['game/today']

		},

		finished: function() {

			return this.$store.getters['game/finished']

		},

		result: function() {

			return this.$store.getters['game/result']

		},

		// only used to time the reveal (a loss holds a beat; a win flips at once)
		solved: function() {

			return !!(this.result && this.result.solved)

		}

	},

	watch: {

		// the route reuses this screen across puzzles (e.g. /archive/:date),
		// so a date change must reload — otherwise the previous puzzle lingers.
		// Reset the reveal so the new puzzle starts on the play surface.
		date: function() {

			this.reveal = false
			this.load()

		},

		// the moment the puzzle finishes, swap the play surface for the reveal
		// IN PLACE (no route change). A win flips immediately; a loss waits a
		// beat so the final tile reveal is seen first.
		finished: function(done) {

			if (!done) {

				this.reveal = false
				return

			}

			var self = this
			var delay = this.solved ? 0 : 1100

			setTimeout(function() {

				self.reveal = true

			}, delay)

		}

	},

	created: function() {

		this.load()

	},

	methods: {

		// load the puzzle (today, or a past date for practice). If it comes back
		// already finished (a locked result, e.g. a reload or cross-device), show
		// the reveal at once — the `finished` watcher's delay is only for a fresh,
		// in-session finish where the last tile animation should be seen.
		load: function() {

			var self = this

			return this.$store.dispatch('game/load', this.date || undefined).then(function() {

				if (self.finished) self.reveal = true

			})

		},

		onPicked: function(pick) {

			if (!pick) return

			// merge so a reload's existing names aren't dropped (reactive set)
			this.$set(this.names, pick.id, pick.name)

		}

	}

}

</script>

<style scoped>

.daily {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	flex: 1;
}

.daily-body {
	flex: 1;
	display: flex;
	justify-content: center;
	padding: 20px 16px;
}

.daily-card {
	width: 100%;
	max-width: 440px;
}

.daily-stage {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

/* No. / TILES / GUESSES sits below the art, centred */
.daily-art-meta {
	margin-top: 10px;
	display: flex;
	justify-content: center;
	text-align: center;
}

.daily-rail {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

/* the editorial prompt heads the play rail; on mobile it sits under the art */
.daily-prompt {
	display: none;
}

.daily-title {
	font-family: var(--font-display);
	font-size: 26px;
	font-weight: 500;
	letter-spacing: -0.01em;
	margin: 0;
	color: var(--ink);
}

.daily-sub {
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	margin: 5px 0 0;
}

.daily-result {
	border: 1px solid var(--hairline);
	border-radius: var(--radius);
	padding: 18px;
	text-align: center;
}

.daily-result.is-solved {
	background: var(--correct-tint);
	border-color: #cfe0c8;
}

.daily-result.is-failed {
	background: var(--wrong-tint);
	border-color: #f0e3df;
}

.daily-result-head {
	font-family: var(--font-display);
	font-size: 24px;
	font-weight: 600;
	color: var(--ink);
}

.daily-result-name {
	font-family: var(--font-display);
	font-size: 18px;
	color: var(--ink);
	margin-top: 4px;
}

.daily-result-score {
	font-family: var(--font-mono);
	font-size: 12px;
	letter-spacing: 0.06em;
	color: var(--muted-2);
	margin-top: 8px;
}

.daily-result-link {
	display: inline-block;
	margin-top: 14px;
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 500;
	color: var(--accent);
	text-decoration: none;
}

.daily-result-link:hover {
	text-decoration: underline;
}

/* desktop: a wide card, art left / play rail right (desktop-daily.png) */
@media (min-width: 720px) {

	/* centre the content vertically in the viewport on big screens */
	.daily-body {
		align-items: center;
		padding: 32px 24px;
	}

	.daily-card {
		max-width: 980px;
	}

	.daily-stage {
		flex-direction: row;
		align-items: flex-start;
		gap: 48px;
	}

	.daily-art {
		flex: none;
		width: 480px;
	}

	.daily-art-meta {
		display: flex;
		justify-content: center;
		margin-top: 14px;
	}

	.daily-rail {
		flex: 1;
		padding-top: 6px;
	}

	.daily-prompt {
		display: block;
	}

}

</style>
